/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRequestSections/ContactInformation/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState, useEffect } from "react";
import styles from "./ContactInformation.module.css";
import { ReactComponent as MailIcon } from "../../../assets/icons/ContactInformation/MailIcon.svg";
import { ReactComponent as WebsiteIcon } from "../../../assets/icons/ContactInformation/WebsiteIcon.svg";
import { useTranslation } from "react-i18next";
import CountryCodeSelector from "../../CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../../data/selectOptions";
import { getSelectedCountryCodeOption } from "../../../utils/common";

const ContactInformation = ({ company, getFieldValidation }) => {
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);

  // Detect tablet view for CountryCodeSelector width
  const [isTablet, setIsTablet] = useState(false);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1023);
      setIsPhone(window.innerWidth < 768);
    };

    checkTablet();
    window.addEventListener("resize", checkTablet);

    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.contactInformation.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.contactInformation.officePhoneLabel")}
          </label>
          <div className={styles.phoneInputContainer}>
            <CountryCodeSelector
              value={getSelectedCountryCodeOption(
                countryCodeOptions,
                company?.telefonoDeOficinaCodigoPais,
              )}
              containerWidth={isTablet ? "85%" : "115%"}
              placeholder={t(
                "components.contactInformation.countryCodeSelectorPlaceholder",
              )}
              disabled={true}
            />
            <input
              type="tel"
              className={`${styles.input} ${
                getFieldValidation && getFieldValidation("telefonoDeOficina")
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.contactInformation.officePhonePlaceholder",
              )}
              value={company?.telefonoDeOficina || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.contactInformation.mainEmailAddressLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <MailIcon
              className={`${styles.inputIcon} ${
                isTablet ? styles.tabletInputIcon : ""
              }`}
            />
            <input
              type="email"
              className={`${styles.input} ${styles.inputWithIcon} ${
                getFieldValidation &&
                getFieldValidation("correoElectronicoPrincipal")
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.contactInformation.mainEmailAddressPlaceholder",
              )}
              value={company?.correoElectronicoPrincipal || ""}
              disabled={true}
              maxLength={100}
            />
          </div>
        </div>
      </div>
      <div className={`${styles.inputRow}`}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.contactInformation.websiteLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <WebsiteIcon className={styles.inputIcon} />
            <input
              type="text"
              className={`${styles.emailInput} ${styles.inputWithIcon} ${
                isTablet || isPhone ? styles.tabletInputIcon : ""
              } ${
                getFieldValidation && getFieldValidation("paginaInternet")
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.contactInformation.websitePlaceholder",
              )}
              value={company?.paginaInternet || ""}
              disabled={true}
              maxLength={2083}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInformation;
